import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWishlistStore } from "@/stores/wishlistStore";
import { ShopifyProduct } from "@/lib/shopify";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { trackAddToWishlist } from "@/lib/analytics";

interface WishlistButtonProps {
  product: ShopifyProduct;
  variantId: string;
  variantTitle: string;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  } | null;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  sku?: string;
  variantImage?: {
    url: string;
    altText?: string | null;
  };
  size?: "sm" | "default" | "lg" | "icon";
  variant?: "default" | "outline" | "ghost";
  className?: string;
  showText?: boolean;
}

export const WishlistButton = ({
  product,
  variantId,
  variantTitle,
  price,
  compareAtPrice,
  selectedOptions,
  sku,
  variantImage,
  size = "icon",
  variant = "ghost",
  className,
  showText = false,
}: WishlistButtonProps) => {
  const { addItem, removeItem, isInWishlist } = useWishlistStore();
  
  const isWishlisted = isInWishlist(variantId);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isWishlisted) {
      removeItem(variantId);
      toast.success("Removed from wishlist");
    } else {
      addItem({
        product,
        variantId,
        variantTitle,
        price,
        compareAtPrice,
        selectedOptions,
        sku,
        variantImage,
      });
      trackAddToWishlist(product, { id: variantId, sku, title: variantTitle, price });
      toast.success("Added to wishlist");
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={cn(
        "transition-all",
        isWishlisted && "text-red-500 hover:text-red-600",
        className
      )}
      onClick={handleClick}
      title={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
    >
      <Heart 
        className={cn(
          "h-5 w-5 transition-all",
          isWishlisted && "fill-current"
        )} 
      />
      {showText && (
        <span className="ml-2">
          {isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
        </span>
      )}
    </Button>
  );
};
